import type { CSSProperties } from "react";
import { getBrandPalette, type BrandVariant } from "../tokens/brand";

export interface TenetLogoLoaderProps {
  variant?: BrandVariant;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  svgClassName?: string;
}

const sizePixels = {
  xs: 16,
  sm: 24,
  md: 40,
  lg: 64,
} as const;

export const TenetLogoLoader = ({
  variant = "light",
  size = "sm",
  className,
  svgClassName,
}: TenetLogoLoaderProps) => {
  const palette = getBrandPalette(variant);

  return (
    <div
      className={`tenet-logo-loader relative inline-flex items-center justify-center ${className ?? ""}`}
    >
      <style>{`
        @keyframes tenet-loader-core-rotate {
          0%, 28% { transform: rotate(0deg); }
          48%, 70% { transform: rotate(180deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes tenet-loader-top-swap {
          0%, 34% { transform: translateY(0px); }
          46%, 72% { transform: translateY(60px); }
          100% { transform: translateY(0px); }
        }

        @keyframes tenet-loader-bottom-swap {
          0%, 34% { transform: translateY(0px); }
          46%, 72% { transform: translateY(-60px); }
          100% { transform: translateY(0px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .tenet-logo-loader .core-anim,
          .tenet-logo-loader .top-bar-anim,
          .tenet-logo-loader .bottom-bar-anim {
            animation: none !important;
          }
        }
      `}</style>
      <svg
        viewBox="0 0 200 200"
        className={svgClassName ?? ""}
        style={{
          width: `${sizePixels[size]}px`,
          height: `${sizePixels[size]}px`,
          display: "block",
        }}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          cx="100"
          cy="100"
          r="50"
          fill="none"
          stroke={palette.primary}
          strokeWidth="0.5"
          opacity="0.2"
        />

        <g opacity="0.34">
          <line
            x1="60"
            y1="40"
            x2="60"
            y2="160"
            stroke={palette.primary}
            strokeWidth="1"
          />
          <line
            x1="100"
            y1="40"
            x2="100"
            y2="160"
            stroke={palette.primary}
            strokeWidth="1"
          />
          <line
            x1="140"
            y1="40"
            x2="140"
            y2="160"
            stroke={palette.primary}
            strokeWidth="1"
          />
          <line
            x1="40"
            y1="70"
            x2="160"
            y2="70"
            stroke={palette.secondary}
            strokeWidth="1"
          />
          <line
            x1="40"
            y1="100"
            x2="160"
            y2="100"
            stroke={palette.secondary}
            strokeWidth="1"
          />
          <line
            x1="40"
            y1="130"
            x2="160"
            y2="130"
            stroke={palette.secondary}
            strokeWidth="1"
          />
        </g>

        <g
          className="core-anim"
          style={
            {
              transformOrigin: "100px 100px",
              animation:
                "tenet-loader-core-rotate 1.65s cubic-bezier(0.4, 0, 0.2, 1) infinite",
            } as CSSProperties
          }
        >
          <rect
            x="90"
            y="60"
            width="20"
            height="80"
            fill={palette.primary}
            rx="2"
          />
          <rect
            x="60"
            y="60"
            width="80"
            height="20"
            fill={palette.primary}
            rx="2"
            className="top-bar-anim"
            style={{
              animation:
                "tenet-loader-top-swap 1.65s cubic-bezier(0.22, 1, 0.36, 1) infinite",
            }}
          />
          <rect
            x="60"
            y="120"
            width="80"
            height="20"
            fill={palette.secondary}
            rx="2"
            opacity="0.75"
            className="bottom-bar-anim"
            style={{
              animation:
                "tenet-loader-bottom-swap 1.65s cubic-bezier(0.22, 1, 0.36, 1) infinite",
            }}
          />
        </g>

        <g opacity="0.5" strokeDasharray="4,4">
          <line
            x1="60"
            y1="70"
            x2="60"
            y2="130"
            stroke={palette.primary}
            strokeWidth="1.5"
          />
          <line
            x1="140"
            y1="70"
            x2="140"
            y2="130"
            stroke={palette.primary}
            strokeWidth="1.5"
          />
        </g>
      </svg>
    </div>
  );
};
